<template>
  <div class="dashboard_page_container">
    <a :href="warningUrl" v-if="isWarningVisible" style="margin-bottom: 1.5rem;">
      <div class="warning badge-warning" style="padding: 14px 18px; border-radius: var(--radius-md); display: flex; align-items: center; gap: 10px;">
        <span class="badge badge-warning" style="font-size: 13px;">Atención</span>
        <span>{{ warningMessage }}</span>
      </div>
    </a>

    <div class="page-title-row">
      <h2 style="margin-bottom: 0;">Facturación y Pagos</h2>
      <a :href="stripeUrl" target="_blank" v-if="stripeUrl" class="main_button-green">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm14 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z"/>
          <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1z"/>
        </svg>
        Panel de Stripe
      </a>
    </div>

    <!-- Stat Cards Summary -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.25rem; margin-bottom: 2rem;">
      <div class="stat-card" v-if="amountPendingToSendToStripe !== 0">
        <span class="stat-card-label">Pendiente Click & Plan → Stripe</span>
        <span class="stat-card-value" style="color: var(--primary-color);">{{ formatAmount(Number(amountPendingToSendToStripe)) }}€</span>
        <span class="stat-card-badge badge-warning">En proceso</span>
      </div>

      <div class="stat-card">
        <span class="stat-card-label">Pendiente Stripe → Banco</span>
        <span class="stat-card-value">{{ formatAmount(Number(pendingAmountInStripe)) }}€</span>
        <span class="stat-card-badge badge-success">Programado</span>
      </div>
    </div>

    <!-- Facturas Section -->
    <div style="margin-top: 1rem; margin-bottom: 2.5rem;">
      <h3 style="margin-bottom: 0.75rem;">Facturas Emitidas</h3>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Importe total</th>
              <th>Gastos de gestión</th>
              <th>Total liquidado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="(invoices.length === 0 && !loadingStore.isLoading)">
              <td colspan="4" style="text-align: center; padding: 2.5rem; color: var(--text-color-light);">
                No tienes facturas emitidas.
              </td>
            </tr> 
            <tr
              v-for="(item, index) in invoices"
              :key="index"
              :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
              @click="invoiceClicked(item)"
            >
              <td style="font-weight: 600;">{{ formatearMesAno(item.month) }}</td>
              <td>{{ formatAmount(item.amount) }}€</td>
              <td>{{ formatAmount(item.gg) }}€</td>
              <td style="font-weight: 700; color: var(--primary-color);">{{ formatAmount(item.amount - item.gg) }}€</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagos Section -->
    <div style="margin-bottom: 2.5rem;">
      <h3 style="margin-bottom: 0.75rem;">Historial de Pagos y Transferencias</h3>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>Fecha envío</th>
              <th>Fecha estimada recepción</th>
              <th>Cantidad</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="(stripeTransfers.length === 0 && !loadingStore.isLoading)">
              <td colspan="3" style="text-align: center; padding: 2.5rem; color: var(--text-color-light);">
                No tienes transferencias pendientes.
              </td>
            </tr> 
            <tr
              v-for="(item, index) in stripeTransfers"
              :key="index"
              :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
              @click="stripePaymentClicked(item)"
            >
              <td>{{ item.created_date }}</td>
              <td>{{ item.arrival_date }}</td>
              <td style="font-weight: 700;">{{ formatAmount(item.amount) }}€</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Info Banner -->
    <div class="card" style="background: var(--very-clear-gray); border: 1px solid var(--border-color); margin-top: 1rem;">
      <div class="flex items-center gap-sm mb-xs">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="var(--text-color-light)" viewBox="0 0 16 16">
          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
        </svg>
        <strong style="color: var(--text-color-dark); font-size: 14px;">Información sobre el procesamiento de pagos</strong>
      </div>
      <p style="margin-bottom: 0; font-size: 13.5px; color: var(--text-color-light); line-height: 1.6;">
        Stripe es nuestra herramienta para gestionar los pagos. Los usuarios pagan nuestros planes a través de esta plataforma, que a su vez gestiona la transferencia a tu cuenta bancaria partner.<br>
        Si has canjeado algún código hoy, la transferencia programada se reflejará en este panel una vez procesada por Stripe (habitualmente al día siguiente).
      </p>
    </div>

    <ErrorPopup :errorMessage="error" />
    <PaymentDetailsPopup :paymentInfo="paymentInfo" :movements="movements"/>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { refreshToken } from "@/services/authService";

import ErrorPopup from "@/components/common/ErrorPopup.vue";
import PaymentDetailsPopup from "./common/PaymentDetailsPopup.vue";

const error = ref(null);
const loadingStore = useLoadingStore();
const navigationStore = useNavigationStore();
const router = useRouter();
const isWarningVisible = ref(false);
const warningUrl = ref("");
const warningMessage = ref("");
const stripeUrl = ref("");
const amountPendingToSendToStripe = ref(0);
const pendingAmountInStripe = ref(0);
const stripeTransfers = ref([]);
const paymentInfo = ref(null);
const movements = ref(null);
const invoices = ref([]);

const getStripeStatus = async () => {
  loadingStore.setLoading(true);
  error.value = null;
  warningUrl.value = null;
  warningMessage.value = null;
  isWarningVisible.value = false;

  try {
    let response = await fetchStripeStatus();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchStripeStatus();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo el estado de tu cuenta.";
    }

    const data = await response.json();
    navigationStore.setUserType(data.isOptionalCompany ? "optional" : "main");

    if (data.warningMessage != null) {
      warningUrl.value = data.warningUrl;
      warningMessage.value = data.warningMessage;
      isWarningVisible.value = true;
    } else {
      isWarningVisible.value = false;
    }
    amountPendingToSendToStripe.value = Number(data.amountPendingToSendToStripe);
    pendingAmountInStripe.value = Number(data.amountPendingToBeTransfered);
    stripeTransfers.value = data.stripeTransfers;
    invoices.value = data.invoices;
  } catch (exception) {
      // do nothing
  } finally {
    loadingStore.setLoading(false);
  }
  getStripeDashboardUrl();
};

const getStripeDashboardUrl = async () => {
  stripeUrl.value = "";

  try {
    let response = await fetchStripeDashboardUrl();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchStripeDashboardUrl();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    const data = await response.json();

    if (data.url != null) {
      stripeUrl.value = data.url;
    }
  } catch (exception) {
      // do nothing
  }
};

async function fetchStripeDashboardUrl() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_dashboard_url", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id')
    }),
  });
}

async function fetchStripeStatus() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_account_status", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id')
    }),
  });
}

navigationStore.setNavigationPosition(2);
getStripeStatus();

function formatAmount(valor) {
  return new Intl.NumberFormat('es-ES', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(valor);
}

async function invoiceClicked(item){
  loadingStore.setLoading(true);
  try {
    let response = await fetchInvoice(item.month);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchInvoice(item.month);
      } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
      }
    }

    loadingStore.setLoading(false);

    if (!response.ok) {
      error.value = "Se ha producido un error descargando la factura.";
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch (exception) {
    error.value = exception.message;
    paymentInfo.value = null;
  } finally {
    loadingStore.setLoading(false);
  }
}

async function fetchInvoice(month) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_invoice", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "date": month
    }),
  });
}

async function stripePaymentClicked(item) {
  paymentInfo.value = null;
  movements.value = null;
  loadingStore.setLoading(true);

  try {
    let response = await fetchPayoutInfo(item.id);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchPayoutInfo(item.id);
      } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
      }
    }

    loadingStore.setLoading(false);

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo la información del pago.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
    } else {
      error.value = null;
      paymentInfo.value = item;
      movements.value = data.datos;
      history.pushState({ popupOpen: true }, "");
    }
  } catch (exception) {
    error.value = exception.message;
    paymentInfo.value = null;
  } finally {
    loadingStore.setLoading(false);
  }
}

async function fetchPayoutInfo(id) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_payout_info", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "payout_id": id
    }),
  });
}

function formatearMesAno(mesAno) {
  const [anio, mes] = mesAno.split("-");
  const fecha = new Date(anio, mes - 1); // Mes va de 0 (enero) a 11 (diciembre)

  return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' })
    .format(fecha)
    .replace(/^./, str => str.toUpperCase()); // Capitaliza la primera letra
}

</script>

<style scoped>
table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: var(--gray);
  color: white;
  width: 25%;
  border: 0px;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

.opaque {
  opacity: 1;
}

.translucid {
  opacity: 0.5;
}

.filter-search {
  width: 300px;
}

.warning {
  border-radius: 10px;
  background-color: #ffe00069;
  color: var(--gray);
  padding: 10px 20px 8px 20px;
}

</style>
